import { NODE_SIZE } from './definitionTransform';

/**
 * How tall a card drawing a collaboration graph is (task 1324, shared by task
 * 1338). One rule for the run page's Path card and the Config tab's Definition
 * canvas: the card grows to the laid-out graph at the zoom its width dictates,
 * between a minimum and the window.
 */

/**
 * The width and height the laid-out graph covers, from its nodes' top-left
 * positions plus each node's box: the size the flow library measured when it
 * has one, otherwise the box dagre lays out. No nodes covers nothing.
 */
export function graphExtent(
  nodes: ReadonlyArray<{ position: { x: number; y: number }; measured?: { width?: number; height?: number } }>
): {
  width: number;
  height: number;
} {
  if (nodes.length === 0) return { width: 0, height: 0 };
  const widthOf = (node: (typeof nodes)[number]) => node.measured?.width || NODE_SIZE.WIDTH;
  const heightOf = (node: (typeof nodes)[number]) => node.measured?.height || NODE_SIZE.HEIGHT;
  return {
    width:
      Math.max(...nodes.map((node) => node.position.x + widthOf(node))) -
      Math.min(...nodes.map((node) => node.position.x)),
    height:
      Math.max(...nodes.map((node) => node.position.y + heightOf(node))) -
      Math.min(...nodes.map((node) => node.position.y)),
  };
}

/** The fit padding and zoom cap, shared by the canvases and their height. */
export const PATH_FIT_PADDING = 0.15;
export const PATH_FIT_MAX_ZOOM = 1;
export const PATH_CANVAS_MIN_HEIGHT = 320;
export const PATH_CANVAS_MAX_HEIGHT = 900;
/** The Definition canvas's working minimum while it is being edited (task 1338). */
export const DEFINITION_CANVAS_EDIT_MIN_HEIGHT = 560;

/**
 * How tall the card is (task 1324): the graph's height at the zoom the card's
 * width dictates (capped at 100%), plus the fit padding, clamped to the
 * minimum (320px unless the caller passes another) and to the smaller of 900px
 * and the window less 160px. The minimum wins over a window too short for it.
 */
export function pathCanvasHeight(
  extent: { width: number; height: number },
  cardWidth: number,
  windowHeight: number,
  minHeight: number = PATH_CANVAS_MIN_HEIGHT
): number {
  const max = Math.max(minHeight, Math.min(PATH_CANVAS_MAX_HEIGHT, windowHeight - 160));
  if (extent.width <= 0 || extent.height <= 0 || cardWidth <= 0) return minHeight;
  const zoom = Math.min(PATH_FIT_MAX_ZOOM, cardWidth / (extent.width * (1 + PATH_FIT_PADDING)));
  const wanted = extent.height * zoom * (1 + PATH_FIT_PADDING);
  return Math.round(Math.min(max, Math.max(minHeight, wanted)));
}
